import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { 
  Volume2, VolumeX, Printer, Settings, Play, ChefHat,
  Bell, Clock, FileText, Save, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

// Sound presets (base64 encoded short beeps)
const SOUND_PRESETS = {
  beep: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH6LkZWTj4aBc2ptdH+JkZWTkIyFfHBpbXR/iJGVk5CMhXxwaW10f4iRlZOQjIV8cGltdH+IkZWTkIyFfHBpbXR/iJGVk5CMhXxwaW10f4g=',
  chime: 'data:audio/wav;base64,UklGRl9vT19teleUklGRl9vT19teleUklGRl9vT19teleUklGRl9vT19teleUklGRl9vT19teleUklGRl9vT19teleUklGRl9vT19tele='
};

export default function KDSSettings() {
  const [settings, setSettings] = useState({
    // Sound Settings
    sound_enabled: true,
    sound_type: 'beep',
    sound_on_new_order: true,
    sound_on_preparing: false,
    sound_on_ready: true,
    custom_sound_url: '',
    sound_volume: 50,
    
    // Print Settings
    print_enabled: false,
    auto_print_trigger: 'manual_only',
    printer_type: 'browser',
    thermal_printer_ip: '',
    thermal_printer_port: 9100,
    
    // Ticket Content
    show_order_number: true,
    show_timestamp: true,
    show_table_info: true,
    show_server_name: false,
    show_order_type: true,
    show_special_notes: true,
    ticket_header_text: '',
    ticket_footer_text: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get('/kds/settings');
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error('Failed to load KDS settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/kds/settings', settings);
      toast.success('KDS settings saved successfully');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const testSound = () => {
    try {
      let soundUrl;
      if (settings.sound_type === 'custom' && settings.custom_sound_url) {
        soundUrl = settings.custom_sound_url;
      } else if (settings.sound_type === 'tts') {
        // Use Web Speech API for TTS
        const utterance = new SpeechSynthesisUtterance('New order received');
        utterance.volume = settings.sound_volume / 100;
        speechSynthesis.speak(utterance);
        toast.success('Playing TTS sound');
        return;
      } else {
        soundUrl = SOUND_PRESETS[settings.sound_type] || SOUND_PRESETS.beep;
      }
      
      const audio = new Audio(soundUrl);
      audio.volume = settings.sound_volume / 100;
      audio.play().then(() => {
        toast.success('Playing test sound');
      }).catch(() => {
        toast.error('Failed to play sound. Check browser permissions.');
      });
    } catch (e) {
      toast.error('Sound playback not supported');
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kds-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ChefHat className="h-6 w-6 text-amber-500" />
            KDS Settings
          </h1>
          <p className="text-muted-foreground">Configure Kitchen Display System sound and print settings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadSettings} data-testid="reset-settings-btn">
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Sound Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Volume2 className="h-5 w-5 text-blue-500" />
              Sound Notifications
            </CardTitle>
            <CardDescription>Configure audio alerts for order status changes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable Sound */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Sounds</Label>
                <p className="text-sm text-muted-foreground">Play audio notifications</p>
              </div>
              <Switch
                checked={settings.sound_enabled}
                onCheckedChange={(val) => updateSetting('sound_enabled', val)}
                data-testid="sound-enabled-switch"
              />
            </div>

            {settings.sound_enabled && (
              <>
                {/* Sound Type */}
                <div className="space-y-2">
                  <Label>Sound Type</Label>
                  <Select
                    value={settings.sound_type}
                    onValueChange={(val) => updateSetting('sound_type', val)}
                  >
                    <SelectTrigger data-testid="sound-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beep">Beep (Simple)</SelectItem>
                      <SelectItem value="chime">Chime (Pleasant)</SelectItem>
                      <SelectItem value="custom">Custom Sound URL</SelectItem>
                      <SelectItem value="tts">Text-to-Speech</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Sound URL */}
                {settings.sound_type === 'custom' && (
                  <div className="space-y-2">
                    <Label>Custom Sound URL</Label>
                    <Input
                      placeholder="https://example.com/sound.mp3"
                      value={settings.custom_sound_url}
                      onChange={(e) => updateSetting('custom_sound_url', e.target.value)}
                      data-testid="custom-sound-url"
                    />
                    <p className="text-xs text-muted-foreground">Enter URL to MP3 or WAV file</p>
                  </div>
                )}

                {/* Volume */}
                <div className="space-y-2">
                  <Label>Volume: {settings.sound_volume}%</Label>
                  <div className="flex items-center gap-3">
                    <VolumeX className="h-4 w-4 text-muted-foreground" />
                    <Slider
                      value={[settings.sound_volume]}
                      onValueChange={([val]) => updateSetting('sound_volume', val)}
                      max={100}
                      step={5}
                      className="flex-1"
                    />
                    <Volume2 className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>

                {/* Sound Triggers */}
                <div className="space-y-3">
                  <Label>Play sound when:</Label>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 rounded border">
                      <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4 text-red-500" />
                        <span className="text-sm">New order arrives</span>
                      </div>
                      <Switch
                        checked={settings.sound_on_new_order}
                        onCheckedChange={(val) => updateSetting('sound_on_new_order', val)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded border">
                      <div className="flex items-center gap-2">
                        <ChefHat className="h-4 w-4 text-amber-500" />
                        <span className="text-sm">Order starts preparing</span>
                      </div>
                      <Switch
                        checked={settings.sound_on_preparing}
                        onCheckedChange={(val) => updateSetting('sound_on_preparing', val)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded border">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-green-500" />
                        <span className="text-sm">Order is ready</span>
                      </div>
                      <Switch
                        checked={settings.sound_on_ready}
                        onCheckedChange={(val) => updateSetting('sound_on_ready', val)}
                      />
                    </div>
                  </div>
                </div>

                {/* Test Sound */}
                <Button variant="outline" onClick={testSound} className="w-full" data-testid="test-sound-btn">
                  <Play className="h-4 w-4 mr-2" />
                  Test Sound
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Print Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5 text-green-500" />
              Printing
            </CardTitle>
            <CardDescription>Configure kitchen ticket printing</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable Printing */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Printing</Label>
                <p className="text-sm text-muted-foreground">Print kitchen tickets</p>
              </div>
              <Switch
                checked={settings.print_enabled}
                onCheckedChange={(val) => updateSetting('print_enabled', val)}
                data-testid="print-enabled-switch"
              />
            </div>

            {settings.print_enabled && (
              <>
                {/* Auto Print Trigger */}
                <div className="space-y-2">
                  <Label>Auto-Print When</Label>
                  <Select
                    value={settings.auto_print_trigger}
                    onValueChange={(val) => updateSetting('auto_print_trigger', val)}
                  >
                    <SelectTrigger data-testid="auto-print-trigger-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual_only">Manual Only (Print button)</SelectItem>
                      <SelectItem value="new_order">New Order Arrives</SelectItem>
                      <SelectItem value="preparing">Order Starts Preparing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Printer Type */}
                <div className="space-y-2">
                  <Label>Printer Type</Label>
                  <Select
                    value={settings.printer_type}
                    onValueChange={(val) => updateSetting('printer_type', val)}
                  >
                    <SelectTrigger data-testid="printer-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="browser">Browser Print Dialog</SelectItem>
                      <SelectItem value="thermal">Thermal Printer (Network)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Thermal Printer Settings */}
                {settings.printer_type === 'thermal' && (
                  <div className="space-y-3 p-3 rounded bg-muted/50">
                    <div className="space-y-2">
                      <Label>Printer IP Address</Label>
                      <Input
                        placeholder="192.168.1.100"
                        value={settings.thermal_printer_ip}
                        onChange={(e) => updateSetting('thermal_printer_ip', e.target.value)}
                        data-testid="thermal-printer-ip"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Port</Label>
                      <Input
                        type="number"
                        placeholder="9100"
                        value={settings.thermal_printer_port}
                        onChange={(e) => updateSetting('thermal_printer_port', parseInt(e.target.value) || 9100)}
                        data-testid="thermal-printer-port"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Note: Direct thermal printing requires the printer to be on the same network.
                    </p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Ticket Content Settings */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-500" />
              Kitchen Ticket Content
            </CardTitle>
            <CardDescription>Customize what information appears on printed tickets</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2">
              {/* Left Column - Toggle Fields */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Show on Ticket:</Label>
                <div className="space-y-2">
                  {[
                    { key: 'show_order_number', label: 'Order Number' },
                    { key: 'show_timestamp', label: 'Timestamp' },
                    { key: 'show_table_info', label: 'Table/Room Info' },
                    { key: 'show_order_type', label: 'Order Type (Dine-in/Takeaway/Room)' },
                    { key: 'show_server_name', label: 'Server Name' },
                    { key: 'show_special_notes', label: 'Special Notes/Instructions' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between p-2 rounded border">
                      <span className="text-sm">{label}</span>
                      <Switch
                        checked={settings[key]}
                        onCheckedChange={(val) => updateSetting(key, val)}
                        data-testid={`toggle-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column - Text Fields */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Ticket Header Text</Label>
                  <Textarea
                    placeholder="e.g., KITCHEN ORDER"
                    value={settings.ticket_header_text}
                    onChange={(e) => updateSetting('ticket_header_text', e.target.value)}
                    rows={2}
                    data-testid="ticket-header-text"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ticket Footer Text</Label>
                  <Textarea
                    placeholder="e.g., Thank you!"
                    value={settings.ticket_footer_text}
                    onChange={(e) => updateSetting('ticket_footer_text', e.target.value)}
                    rows={2}
                    data-testid="ticket-footer-text"
                  />
                </div>

                {/* Ticket Preview */}
                <div className="mt-4">
                  <Label className="text-base font-medium mb-2 block">Ticket Preview:</Label>
                  <div className="bg-white border-2 border-dashed rounded p-4 font-mono text-sm max-w-xs">
                    {settings.ticket_header_text && (
                      <div className="text-center font-bold mb-2 border-b pb-2">
                        {settings.ticket_header_text}
                      </div>
                    )}
                    {settings.show_order_number && <div>Order #: 001234</div>}
                    {settings.show_timestamp && <div>Time: 12:30 PM</div>}
                    {settings.show_table_info && <div>Table: 5</div>}
                    {settings.show_order_type && <div>Type: Dine-in</div>}
                    {settings.show_server_name && <div>Server: John</div>}
                    <div className="border-t my-2 pt-2">
                      <div>2x Butter Chicken</div>
                      <div>1x Naan</div>
                      <div>1x Lassi</div>
                    </div>
                    {settings.show_special_notes && (
                      <div className="border-t mt-2 pt-2 text-xs">
                        Note: Extra spicy
                      </div>
                    )}
                    {settings.ticket_footer_text && (
                      <div className="text-center mt-2 border-t pt-2">
                        {settings.ticket_footer_text}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
